import React, { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';

import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';

import { Product } from 'api/types/product';
import { storeReorderProducts } from 'api/store_sdk';

import ProductCardAdmin from 'components/ProductCard/ProductCardAdmin';
import { isExternalLink, isPreorderProduct, isRequest, ProductLocalStatus } from 'utils/product';

import { message } from 'antd';
import { useLoadingBackdrop } from 'hooks/useLoadingBackdrop';

import inactiveFolderImage from './images/inactiveFolder.png';

import './ProductsList.scss';

interface IProductsList {
  data: Product[];
  currency: string;
  openedProductId?: number;
  onEdit: (product: Partial<Product>) => void;
  onDelete: (product: Partial<Product>) => void;
  onPreview: (product: Partial<Product>) => void;
  onPropagate?: (product: Partial<Product>) => void;
  onMassDelete?: (product: Partial<Product>) => void;
  newLocalProducts: Record<string, ProductLocalStatus>;
  storeId: number;
  updateStoreProducts: () => void;
  productsAreReordering: boolean;
}

const ProductsList = ({
  data,
  currency,
  openedProductId,
  onEdit,
  onDelete,
  onPreview,
  onPropagate,
  onMassDelete,
  newLocalProducts,
  storeId,
  updateStoreProducts,
  productsAreReordering,
}: IProductsList) => {
  const [showLoading, hideLoading] = useLoadingBackdrop();

  const [products, setProducts] = useState<Product[]>(data);
  const [dragProduct, setDragProduct] = useState<Product | null>(null);
  const [isInactiveFolderOpen, setIsInactiveFolderOpen] = useState(false);

  useEffect(() => {
    setProducts(data);
  }, [data]);

  const activeProducts = products.filter((product) => product.isActive);
  const inactiveProducts = products.filter((product) => !product.isActive);

  const handleSaveProductOrder = useCallback(
    (event) => {
      if (!productsAreReordering) {
        setDragProduct(null);
        return;
      }

      const activeId = event.active.id;
      const overId = event.over?.id;

      const fromIndex = activeProducts.findIndex((p) => p.id === activeId);
      let toIndex = -1;

      if (overId) {
        toIndex = activeProducts.findIndex((p) => p.id === overId);
      } else {
        toIndex = activeProducts.length - 1;
      }

      if (toIndex === -1) {
        toIndex = activeProducts.length - 1;
      }

      if (fromIndex === -1 || fromIndex === toIndex) {
        setDragProduct(null);
        return;
      }

      setTimeout(() => {
        showLoading();
      }, 0);

      let updatedActiveProducts = [...activeProducts];
      const [movedProduct] = updatedActiveProducts.splice(fromIndex, 1);
      updatedActiveProducts.splice(toIndex, 0, movedProduct);

      updatedActiveProducts = updatedActiveProducts.map((product, index) => ({
        ...product,
        storeArrangement: index,
      }));

      setProducts([...updatedActiveProducts, ...inactiveProducts]);

      const productsPositions = updatedActiveProducts.map((product) => ({
        id: product.id,
        position: product.storeArrangement,
      }));

      storeReorderProducts({ productsPositions, storeId })
        .then(() => {
          message.success('Products order has been successfully updated.');
          updateStoreProducts();
          hideLoading();
          setDragProduct(null);
        })
        .catch((error) => {
          message.error(error.message);
          hideLoading();
          setProducts(products);
          setDragProduct(null);
        });
    },
    [
      storeId,
      updateStoreProducts,
      showLoading,
      hideLoading,
      products,
      activeProducts,
      inactiveProducts,
      productsAreReordering,
    ],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 5,
        delay: 250,
        tolerance: 5,
      },
    }),
  );

  const handleDragStart = useCallback(
    (event) => {
      if (!productsAreReordering) {
        return;
      }

      setDragProduct(_.find(activeProducts, ['id', event.active.id]) || null);
    },
    [activeProducts, productsAreReordering],
  );

  return (
    <div className="DashboardStoreProducts_list" id="DashboardStoreProducts_list">
      <div
        className={`DashboardStoreProducts_list-grid ${
          productsAreReordering ? 'DragAndDropContainer' : ''
        } ${_.isNil(dragProduct) ? '' : 'ActiveDrag'}`}
      >
        <DndContext
          onDragEnd={handleSaveProductOrder}
          sensors={sensors}
          onDragStart={handleDragStart}
        >
          <SortableContext
            items={activeProducts.map((product) => product.id)}
            strategy={rectSortingStrategy}
          >
            {activeProducts.map((product) => (
              <ProductCardAdmin
                key={product.id}
                itemId={product.id}
                className="DashboardStoreProducts_productCardAdmin"
                title={product.name}
                amount={product.quantity}
                price={Number(product.price)}
                currency={currency}
                imgSrc={product.image}
                disabled={!_.isNil(openedProductId)}
                onEdit={() => onEdit({ id: product.id })}
                onDelete={() => onDelete({ id: product.id, name: product.name })}
                onPreview={() => onPreview({ id: product.id })}
                onPropagate={() => onPropagate({ id: product.id })}
                onMassDelete={() => onMassDelete({ id: product.id })}
                isPreorder={isPreorderProduct(product)}
                isRequest={isRequest(product)}
                isLink={isExternalLink(product)}
                ownerCommission={product.ownerLocalProductCommission}
                category={product?.category}
                subcategory={product?.subcategory}
                isActive={product?.isActive}
                productStatus={newLocalProducts?.[product.id]}
                hasQuantity={product?.hasQuantity}
                storeId={product.store.id}
                productGroup={product.productGroup || null}
                updateStoreProducts={updateStoreProducts}
                enableDragAndDrop={productsAreReordering}
                priceType={product.priceType}
              />
            ))}
          </SortableContext>

          <DragOverlay modifiers={[restrictToParentElement]}>
            {dragProduct ? (
              <ProductCardAdmin
                itemId={dragProduct.id}
                className="DashboardStoreProducts_productCardAdmin DragableProductCard"
                title={dragProduct.name}
                amount={dragProduct.quantity}
                price={Number(dragProduct.price)}
                currency={currency}
                imgSrc={dragProduct.image}
                disabled={!_.isNil(openedProductId)}
                onEdit={() => onEdit({ id: dragProduct.id })}
                onDelete={() => onDelete({ id: dragProduct.id, name: dragProduct.name })}
                onPreview={() => onPreview({ id: dragProduct.id })}
                onPropagate={() => onPropagate({ id: dragProduct.id })}
                onMassDelete={() => onMassDelete({ id: dragProduct.id })}
                isPreorder={isPreorderProduct(dragProduct)}
                isRequest={isRequest(dragProduct)}
                isLink={isExternalLink(dragProduct)}
                ownerCommission={dragProduct.ownerLocalProductCommission}
                category={dragProduct?.category}
                subcategory={dragProduct?.subcategory}
                isActive={dragProduct?.isActive}
                productStatus={newLocalProducts?.[dragProduct.id]}
                hasQuantity={dragProduct?.hasQuantity}
                storeId={dragProduct.store.id}
                productGroup={dragProduct.productGroup || null}
                updateStoreProducts={updateStoreProducts}
                enableDragAndDrop={productsAreReordering}
                priceType={dragProduct.priceType}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {inactiveProducts.length > 0 && (
        <div className="DashboardStoreProducts_inactiveFolderWrapper">
          <div
            className="DashboardStoreProducts_inactiveFolderCard"
            onClick={() => setIsInactiveFolderOpen(true)}
          >
            <div className="DashboardStoreProducts_inactiveFolderBadge">Inactive</div>

            <div className="DashboardStoreProducts_inactiveFolderIcon">
              <img src={inactiveFolderImage} alt="Inactive folder" />
            </div>

            <div className="DashboardStoreProducts_inactiveFolderTitleText">
              Inactive hidden tiles
            </div>

            <div className="DashboardStoreProducts_inactiveFolderDescription">
              Open to reactivate any tile
            </div>
          </div>
        </div>
      )}

      {isInactiveFolderOpen && (
        <div className="DashboardStoreProducts_inactiveFolderOverlay">
          <div className="DashboardStoreProducts_inactiveFolderModal">
            <button
              type="button"
              className="DashboardStoreProducts_inactiveFolderClose"
              onClick={() => setIsInactiveFolderOpen(false)}
            >
              ×
            </button>

            <div className="DashboardStoreProducts_inactiveFolderTitle">
              Inactive Folder ({inactiveProducts.length})
            </div>
            <div className="DashboardStoreProducts_inactiveFolderSubtitle">
              These tiles are hidden from guests.
            </div>

            <div className="DashboardStoreProducts_inactiveFolderGrid">
              {inactiveProducts.map((product) => (
                <ProductCardAdmin
                  key={product.id}
                  itemId={product.id}
                  className="DashboardStoreProducts_productCardAdmin"
                  title={product.name}
                  amount={product.quantity}
                  price={Number(product.price)}
                  currency={currency}
                  imgSrc={product.image}
                  disabled={!_.isNil(openedProductId)}
                  onEdit={() => onEdit({ id: product.id })}
                  onDelete={() => onDelete({ id: product.id, name: product.name })}
                  onPreview={() => onPreview({ id: product.id })}
                  onPropagate={() => onPropagate?.({ id: product.id })}
                  onMassDelete={() => onMassDelete?.({ id: product.id })}
                  isPreorder={isPreorderProduct(product)}
                  isRequest={isRequest(product)}
                  isLink={isExternalLink(product)}
                  ownerCommission={product.ownerLocalProductCommission}
                  category={product?.category}
                  subcategory={product?.subcategory}
                  isActive={product?.isActive}
                  productStatus={newLocalProducts?.[product.id]}
                  hasQuantity={product?.hasQuantity}
                  storeId={product.store.id}
                  productGroup={product.productGroup || null}
                  updateStoreProducts={updateStoreProducts}
                  enableDragAndDrop={false}
                  priceType={product.priceType}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsList;
